/**
 * Planejador principal de Inteligência Artificial do OZ Emblem.
 *
 * Determina a melhor ação para uma unidade ou para todo o turno da CPU,
 * respeitando perfis táticos, dificuldades, restrições de tempo e determinismo.
 */
import { itemHealFor, itemUseOf } from '../actions.js';
import { buildOccupancy } from '../grid.js';
import { reachableTiles, standableTiles } from '../movement.js';
import { livingUnits, weaponOf } from '../state.js';
import { curableBy, hasEffect } from '../states.js';
import { isHostile } from '../types.js';
import { comparePositions } from './evaluator.js';
import { evaluateActionsForProfile } from './profiles.js';
import { buildThreatMap } from './threat.js';
const DEFAULT_OPTIONS = {
    difficulty: 'normal',
    timeBudgetMs: 200,
    patrolWaypoints: [],
};
/**
 * Escolhe a ação tática de uma única unidade da CPU.
 */
export function chooseAiAction(state, content, unit, options = {}) {
    if (!unit.alive || unit.hasActed || hasEffect(unit.states, 'nao_age')) {
        return { type: 'mover', unitId: unit.id, path: [] };
    }
    const opts = { ...DEFAULT_OPTIONS, ...options };
    const occupancy = buildOccupancy(state.units);
    const movement = content.classes.get(unit.classId)?.move ?? 5;
    const range = reachableTiles(state.map, content, occupancy, unit.position, movement, unit.team, unit.size);
    const spots = standableTiles(range, occupancy, unit.id, unit.size).sort(comparePositions);
    const enemies = livingUnits(state)
        .filter((u) => isHostile(unit.team, u.team))
        .sort((a, b) => a.id - b.id);
    const allies = livingUnits(state, unit.team).sort((a, b) => a.id - b.id);
    /*
     * Beber a poção vem ANTES de qualquer plano de ataque.
     *
     * Um Fire Emblem se joga com a bolsa tanto quanto com a espada, e a IA
     * ignorava a bolsa por completo: a unidade a dois pontos de morrer carregava
     * três poções e ia atacar assim mesmo. Quem está muito ferido e tem com que
     * se curar se cura — e só se cura quando isso muda alguma coisa, senão a
     * unidade passaria o turno bebendo em vez de lutar.
     */
    const potion = bestPotion(content, unit);
    if (potion) {
        const threat = buildThreatMap(state, content, unit, enemies);
        const missing = unit.stats.hp - unit.currentHp;
        const ratio = unit.currentHp / unit.stats.hp;
        const incoming = threat.damageAt(unit.position);
        // Em perigo de morrer na fase seguinte, ou machucado o bastante para a
        // poção não ser desperdício. O limiar sobe com a dificuldade: uma IA fácil
        // erra o momento de se curar, como um jogador iniciante.
        const panicAt = opts.difficulty === 'dificil' ? 0.55 : opts.difficulty === 'normal' ? 0.42 : 0.28;
        const lethal = incoming >= unit.currentHp;
        if ((ratio <= panicAt || lethal) && missing >= Math.min(potion.heal, 6)) {
            return { type: 'usar_item', unitId: unit.id, path: [], index: potion.index };
        }
    }
    /*
     * E a condição que atrapalha a próxima jogada também se cura — mas só a que
     * atrapalha de fato. Na fácil, não: um iniciante esquece o antídoto na bolsa.
     */
    if (opts.difficulty !== 'facil') {
        const cure = bestCure(content, unit, worthCuring(content, unit));
        if (cure !== null)
            return { type: 'usar_item', unitId: unit.id, path: [], index: cure };
    }
    return evaluateActionsForProfile({
        state,
        content,
        unit,
        range,
        spots,
        enemies,
        allies,
        options: opts,
        threat: buildThreatMap(state, content, unit, enemies),
    });
}
/**
 * A melhor poção da bolsa para o estrago atual.
 *
 * A menor que ainda resolve, e não a mais forte: gastar o Elixir Restaurador
 * para repor 8 pontos de vida é jogar fora a carta que salva o chefe de fase.
 */
function bestPotion(content, unit) {
    const missing = unit.stats.hp - unit.currentHp;
    if (missing <= 0)
        return null;
    let best = null;
    for (const [index, item] of unit.inventory.entries()) {
        if (item.usesLeft <= 0)
            continue;
        const use = itemUseOf(content, item);
        if (!use)
            continue;
        // O que cura no máximo, e não o que cura agora: é a régua de desperdício.
        const heal = use.heal + Math.floor(unit.stats.hp * use.healRate);
        if (heal <= 0 || itemHealFor(use, unit) <= 0)
            continue;
        if (best === null) {
            best = { index, heal };
            continue;
        }
        // Prefere a que cobre o estrago com menos desperdício; se nenhuma cobre,
        // a maior disponível.
        const bestWaste = best.heal >= missing ? best.heal - missing : Infinity;
        const waste = heal >= missing ? heal - missing : Infinity;
        if (waste < bestWaste || (waste === Infinity && bestWaste === Infinity && heal > best.heal)) {
            best = { index, heal };
        }
    }
    return best;
}
/**
 * As condições desta unidade que valem o turno de curar.
 *
 * Curar gasta a jogada inteira, então só entra o que vai continuar custando:
 * veneno com dois turnos ou mais pela frente, queimadura com um, cegueira em
 * quem luta e silêncio em quem depende de magia. Paralisia e sono não
 * aparecem aqui — quem os tem não age, e não bebe nada.
 */
function worthCuring(content, unit) {
    const weapon = weaponOf(content, unit);
    const out = [];
    for (const state of unit.states) {
        const left = state.turnsLeft ?? Infinity;
        switch (state.condition) {
            case 'envenenado':
                if (left >= 2)
                    out.push(state.condition);
                break;
            case 'queimando':
                if (left >= 1)
                    out.push(state.condition);
                break;
            case 'cego':
                if (left >= 1 && weapon && !weapon.heals)
                    out.push(state.condition);
                break;
            case 'silenciado':
                if (left >= 1 && (weapon?.magic || weapon?.heals))
                    out.push(state.condition);
                break;
            default:
                break;
        }
    }
    return out;
}
/**
 * O item que cura o que precisa ser curado, gastando o mínimo.
 *
 * Entre dois que resolvem, o que cura menos coisas: a Água Pura, que tira
 * tudo, fica para o dia em que vier tudo junto.
 */
function bestCure(content, unit, wanted) {
    if (wanted.length === 0)
        return null;
    let best = null;
    for (const [index, item] of unit.inventory.entries()) {
        const use = itemUseOf(content, item);
        if (!use || use.cures.length === 0)
            continue;
        const covers = curableBy(unit.states.filter((s) => wanted.includes(s.condition)), use.cures).length;
        if (covers === 0)
            continue;
        if (best === null ||
            covers > best.covers ||
            (covers === best.covers && use.cures.length < best.breadth)) {
            best = { index, covers, breadth: use.cures.length };
        }
    }
    return best?.index ?? null;
}
/**
 * Próxima unidade da equipe que ainda pode agir, em ordem estável de identificador.
 */
export function nextAiActor(state, team = state.phase) {
    return livingUnits(state, team)
        .filter((u) => !u.hasActed && !hasEffect(u.states, 'nao_age'))
        .sort((a, b) => a.id - b.id)[0];
}
//# sourceMappingURL=planner.js.map